import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { XCircle, RefreshCw, Home, MessageCircle } from 'lucide-react';

const PaymentFailedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const subscriptionId = searchParams.get('subscription_id');

  const t = {
    en: {
      title: 'Payment Failed',
      subtitle: 'Your payment could not be processed',
      message: 'Don\'t worry! Your subscription has been saved. You can try again or contact us for assistance.',
      reasons: 'This might have happened because:',
      reason1: 'The payment was cancelled',
      reason2: 'Card was declined',
      reason3: 'Session expired',
      tryAgain: 'Try Again',
      goHome: 'Go to Homepage',
      needHelp: 'Need help?',
      contactUs: 'Contact us on WhatsApp'
    },
    ar: {
      title: 'فشل الدفع',
      subtitle: 'لم نتمكن من معالجة الدفع',
      message: 'لا تقلق! تم حفظ اشتراكك. يمكنك المحاولة مرة أخرى أو التواصل معنا للمساعدة.',
      reasons: 'قد يكون هذا حدث بسبب:',
      reason1: 'تم إلغاء الدفع',
      reason2: 'تم رفض البطاقة',
      reason3: 'انتهت صلاحية الجلسة',
      tryAgain: 'حاول مرة أخرى',
      goHome: 'العودة للصفحة الرئيسية',
      needHelp: 'تحتاج مساعدة؟',
      contactUs: 'تواصل معنا على واتساب'
    }
  };

  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-red-50 to-orange-100 py-12 px-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Failed Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-8 text-center text-white">
            <XCircle className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">{t[language].title}</h1>
            <p className="text-xl opacity-90">{t[language].subtitle}</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <p className="text-lg text-gray-600 mb-6 text-center">
              {t[language].message}
            </p>

            {/* Possible Reasons */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="font-medium text-gray-700 mb-3">{t[language].reasons}</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                  {t[language].reason1}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                  {t[language].reason2}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                  {t[language].reason3}
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscribe')}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:from-orange-600 hover:to-pink-600 transition-all"
              >
                <RefreshCw className="w-5 h-5" />
                {t[language].tryAgain}
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-200 transition-all"
              >
                <Home className="w-5 h-5" />
                {t[language].goHome}
              </button>
            </div>

            {/* WhatsApp Help */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 mb-3">{t[language].needHelp}</p>
              <a
                href="https://wa.me/96879008872"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                {t[language].contactUs}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailedPage;
